import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Heart } from "lucide-react";
import { toast } from "sonner";

export default function Auth() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back 💙");
      } else {
        await register(form.name, form.email, form.password);
        toast.success("Welcome to the family! You've received 100 AMIR to start.");
      }
      nav("/forum");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-5 py-16">
      <div className="text-center mb-8">
        <span className="grid place-items-center w-14 h-14 rounded-full bg-[#6B8EAC] text-white mx-auto mb-4">
          <Heart size={26} fill="white" />
        </span>
        <h1 className="font-display text-4xl text-[#1A202C]">
          {mode === "login" ? "Welcome back" : "Join our family"}
        </h1>
        <p className="text-[#4A5568] mt-2">
          {mode === "login" ? "Sign in to continue the journey" : "A gentle, supportive community awaits"}
        </p>
      </div>

      <form onSubmit={submit} className="bg-white border border-[#6A0DAD] rounded-2xl p-7 shadow-sm space-y-4">
        {mode === "register" && (
          <Field label="Your name" testid="auth-name">
            <input
              data-testid="auth-name-input"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input"
              placeholder="Jane Doe"
            />
          </Field>
        )}
        <Field label="Email" testid="auth-email">
          <input
            data-testid="auth-email-input"
            required
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="input"
            placeholder="you@email.com"
          />
        </Field>
        <Field label="Password" testid="auth-password">
          <input
            data-testid="auth-password-input"
            required
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="input"
            placeholder="••••••••"
          />
        </Field>

        <button
          data-testid="auth-submit-btn"
          disabled={loading}
          className="w-full bg-[#6B8EAC] text-white hover:bg-[#5A7B96] rounded-full py-3 font-heading font-semibold transition-colors disabled:opacity-60"
        >
          {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
        </button>
      </form>

      <p className="text-center mt-5 text-sm text-[#4A5568]">
        {mode === "login" ? "New here?" : "Already have an account?"}{" "}
        <button
          data-testid="auth-toggle-mode"
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          className="text-[#6B8EAC] font-semibold font-heading"
        >
          {mode === "login" ? "Create an account" : "Sign in"}
        </button>
      </p>

      <style>{`
        .input { width:100%; border:1px solid #6A0DAD; border-radius:0.9rem; padding:0.7rem 0.9rem; font-family:'Manrope',sans-serif; outline:none; transition:border-color .2s; }
        .input:focus { border-color:#6B8EAC; }
      `}</style>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-sm font-heading font-medium text-[#1A202C] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
